/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/scandipwa
 * @link https://github.com/scandipwa/scandipwa
 */

import ProductListQuery from 'Query/ProductList.query';
import {
    CartQuery as SourceCartQuery
} from 'SourceQuery/Cart.query';
import { isSignedIn } from 'Util/Auth';
import { Field, Fragment } from 'Util/Query';

/** @namespace Elganso/Query/Cart/Query */
export class CartQuery extends SourceCartQuery {
    // #region Yeboyebo
    getApplyPointsMutation(values, quoteId) {
        const mutation = new Field('applyPoints')
            .addArgument('values', 'String!', values)
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    getApplyValeMutation(couponCode, quoteId) {
        const mutation = new Field('applyVale')
            .addArgument('coupon_code', 'String!', couponCode)
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    getApplyTarjetaMonederoMutation(tarjetaMonederoCode, quoteId) {
        const mutation = new Field('applyTarjetaMonedero')
            .addArgument('tarjetas_monedero_code', 'String!', tarjetaMonederoCode)
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    updateGiftFromCartMutation(textA, textDe, textText, quoteId) {
        const mutation = new Field('updateGiftQuote')
            .addArgument('gift_from', 'String', textDe)
            .addArgument('gift_to', 'String', textA)
            .addArgument('gift_message', 'String!', textText)
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    removeGiftFromCartMutation(quoteId) {
        const mutation = new Field('removeGiftFromQuote')
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    getAddGiftMutation(textA, textDe, textText, quoteId) {
        const mutation = new Field('addGiftToQuote')
            .addArgument('gift_from', 'String', textDe)
            .addArgument('gift_to', 'String', textA)
            .addArgument('gift_message', 'String!', textText)
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    getRemoveValeMutation(quoteId) {
        const mutation = new Field('removeVale')
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }

    getRemoveTarjetaMonederoMutation(quoteId) {
        const mutation = new Field('removeTarjetaMonedero')
            .addField(this.getCartQuery(quoteId));

        if (!isSignedIn()) {
            mutation.addArgument('guestCartId', 'String', quoteId);
        }

        return mutation;
    }
    // #endregion

    /* Data for new cart */
    _getCartItemProduct() {
        return new Field('product')
            .addFieldList(ProductListQuery._getCartProductInterfaceFields(true));
    }

    _getCartBundleProductFragment() {
        return new Fragment('BundleCartItem')
            .addFieldList([
                this._getBundleOptionsField(),
                this._getProductBundleAttributeptionField(),
                this._getCustomizableOptionsField('bundle_customizable_options')
            ]);
    }

    _getProductBundleAttributeptionField() {
        return new Field('attribute_options')
            .addFieldList(this._getProductBundleAttributeptionFields());
    }

    _getProductBundleAttributeptionFields() {
        return [
            'id',
            'color',
            'size'
        ];
    }

    _getCartConfigurableProductFragment() {
        return new Fragment('ConfigurableCartItem')
            .addFieldList([
                this._getConfigurableOptionsField(),
                this._getConfigurableVarianField(),
                this._getCustomizableOptionsField('configurable_customizable_options')
            ]);
    }

    _getConfigurableVarianField() {
        return new Field('configured_variant')
            .addFieldList(this._getConfigurableVarianFields());
    }

    _getConfigurableVarianFields() {
        return [
            'name',
            'sku'
        ];
    }

    _getPricesFields() {
        return [
            'applied_rule_ids',
            'coupon_code',
            'quote_currency_code',
            this._getGrandTotalField(),
            this._getSubtotalInclTaxField(),
            this._getSubtotalExclTaxField(),
            this._getSubtotalWithDiscountExclTaxField(),
            this._getDiscountField(),
            this._getAppliedTaxesField(),
            'vales_code',
            'vales_total',
            'vales_descripcion',
            'bonosganso_code',
            'mp_reward_earn',
            'mp_reward_spent',
            'mp_reward_discount',
            'mp_reward_applied',
            'reward_config',
            'gift_enabled',
            'gift_from',
            'gift_to',
            'gift_message',
            'tarjetas_monedero_code',
            'tarjetas_monedero_total',
            'tarjetas_monedero_pais'
        ];
    }
}

export default new CartQuery();
